/**
 * GetHelpDuringShiftBottomSheet Component
 *
 * A multi-step bottom sheet that appears when a worker clicks "Get Help" or "Contact Support"
 * (from Account page) or clicks the "Support" pill (from Chat page) while they have an
 * in-progress shift. This intervention guides workers through understanding their shift details
 * and provides options for fixing timesheet issues.
 *
 * Flow:
 * - Step 1: Shows shift details and asks "Need help with this shift?"
 * - Step 2: Shows detailed timeline, facility, and shift information
 * - Step 3: Presents two options for fixing timesheet issues (digital vs paper timesheet)
 *
 * The bottom sheet only appears when ALL of the following conditions are met:
 * - Feature flag TIMECLOCK_INFO_WHEN_CONTACTING_SUPPORT is enabled for the user
 * - Worker has an in-progress shift (start time in past, end time in future)
 * - Worker clicks "Get Help" or "Contact Support" on the Account page, OR clicks "Support" pill in Chat
 *
 * If any condition is not met, the default behavior navigates to /needhelp (legacy flow).
 *
 * Feature Flag: This component is gated behind the LaunchDarkly feature flag
 * "2025-18-orbit-timeclock-info-when-contacting-support". The flag must be enabled
 * for the user in LaunchDarkly for this intervention to appear.
 *
 * Note: This file exceeds the max-lines limit due to the complexity of the multi-step flow.
 * The component is intentionally kept together for maintainability and readability.
 */
/* eslint-disable max-lines */
import { BottomSheet, Pill, Text, type UseModalState } from "@clipboard-health/ui-components";
import { Card, CbhIcon } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Link, Stack } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { differenceInMinutes, min, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { type ActivityItem, ActivityList } from "../../components/ActivityList";
import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { IconButton } from "../../components/IconButton";
import { CurrentShiftStage } from "../../Shift/constants";
import { useShiftDurationWithBreak } from "../../Shift/hooks/useShiftDurationWithBreak";
import { ShiftCardSummaryInfo } from "../../Shift/Open/CardSummaryInfo";
import { TimeSlotIndicator } from "../../Shift/TimeSlotIndicator";
import { getShiftCurrentStage } from "../../Shift/WorkerShift/getShiftCurrentStage";
import { formatTimeWithTimezone } from "../../utils/formatTimeWithTimezone";
import { SHIFT_DETAILS_TIMEKEEPING_FULL_PATH } from "../../WorkerShifts/path";
import { useUpcomingOrInProgressShiftData } from "../hooks/useHasUpcomingOrInProgressShift";

export interface GetHelpDuringShiftBottomSheetProps {
  modalState: UseModalState;
}

/**
 * Represents the three steps in the help intervention flow
 */
type GetHelpStep = "step1" | "step2" | "step3";

// eslint-disable-next-line complexity
export function GetHelpDuringShiftBottomSheet(props: GetHelpDuringShiftBottomSheetProps) {
  const { modalState } = props;
  const [currentStep, setCurrentStep] = useState<GetHelpStep>("step1");
  const { userId } = useDefinedWorker();
  const history = useHistory();

  useLogEffect(APP_V2_USER_EVENTS.GET_HELP_DURING_SHIFT_SHOWN, { workerId: userId });

  // Fetch the in-progress shift data (returns undefined if no shift is in progress)
  const shiftData = useUpcomingOrInProgressShiftData();
  const { paperTimesheetInstructionsSupportLink } = useSupportLinks();
  const { openArticle } = useZendeskArticle();

  // Calculate shift duration and break info - always call hook but with safe defaults
  // This ensures the hook doesn't break if shiftData is undefined
  const shiftDurationData = useShiftDurationWithBreak({
    startDate: shiftData ? parseISO(shiftData.shiftStart) : new Date(),
    endDate: shiftData ? parseISO(shiftData.shiftEnd) : new Date(),
    workplaceId: shiftData?.workplaceId ?? "",
    requiresLunchBreak: shiftData?.shift.facility.requiresLunchBreak ?? false,
  });

  // Determine the current stage of the shift (e.g., CLOCKED_IN, BREAK_STARTED, etc.)
  const currentShiftStage = shiftData?.shift ? getShiftCurrentStage(shiftData.shift) : undefined;

  // Calculate how many minutes have elapsed since shift start (for display in Step 1)
  // Only calculate if shift has actually started (not in NOT_STARTED state)
  const minutesElapsed =
    shiftData && currentShiftStage && currentShiftStage !== CurrentShiftStage.NOT_STARTED
      ? differenceInMinutes(new Date(), parseISO(shiftData.shiftStart))
      : 0;

  /**
   * Calculate arrived and clock in dates for Step 2 timeline display.
   * The "arrived" time is the earlier of geofence entry time or clock in time,
   * matching the logic used on the focus screen for consistency.
   */
  const { arrivedDate, clockInDate, breakStartDate, breakEndDate } = useMemo(() => {
    if (!shiftData?.shift) {
      return {
        arrivedDate: undefined,
        clockInDate: undefined,
        breakStartDate: undefined,
        breakEndDate: undefined,
      };
    }

    const { shift } = shiftData;
    const geofenceEntryTime = isDefined(shift.geofence?.entry)
      ? parseISO(shift.geofence.entry)
      : undefined;

    const clockIn = isDefined(shift.clockInOut?.start)
      ? parseISO(shift.clockInOut.start)
      : undefined;

    const arrived =
      isDefined(geofenceEntryTime) && isDefined(clockIn)
        ? min([geofenceEntryTime, clockIn])
        : clockIn;

    const breakStart = isDefined(shift.lunchInOut?.start)
      ? parseISO(shift.lunchInOut.start)
      : undefined;

    const breakEnd = isDefined(shift.lunchInOut?.end) ? parseISO(shift.lunchInOut.end) : undefined;

    return {
      arrivedDate: arrived,
      clockInDate: clockIn,
      breakStartDate: breakStart,
      breakEndDate: breakEnd,
    };
  }, [shiftData]);

  /**
   * Build activity timeline list for Step 2.
   * Displays chronological events: Arrived, Clock in, Break start, Break end.
   * Only includes activities that have actually occurred (have timestamps).
   */
  const activityItems: ActivityItem[] = useMemo(() => {
    if (!shiftData) {
      return [];
    }

    const activities: ActivityItem[] = [];

    // Arrived activity
    if (isDefined(arrivedDate)) {
      activities.push({
        id: "arrived",
        icon: { type: "icon", iconType: "map-pin" },
        title: "Arrived",
        timestamp: formatTimeWithTimezone(arrivedDate, shiftData.workplaceTimezone, false),
      });
    }

    // Clock in activity
    if (isDefined(clockInDate)) {
      activities.push({
        id: "clock-in",
        icon: { type: "icon", iconType: "clock" },
        title: "Clock in",
        timestamp: formatTimeWithTimezone(clockInDate, shiftData.workplaceTimezone, false),
      });
    }

    // Break activities
    if (isDefined(breakStartDate)) {
      activities.push({
        id: "break-start",
        icon: { type: "icon", iconType: "break" },
        title: "Break start",
        timestamp: formatTimeWithTimezone(breakStartDate, shiftData.workplaceTimezone, false),
      });
    }

    if (isDefined(breakEndDate)) {
      activities.push({
        id: "break-end",
        icon: { type: "icon", iconType: "break" },
        title: "Break end",
        timestamp: formatTimeWithTimezone(breakEndDate, shiftData.workplaceTimezone, false),
      });
    }

    return activities;
  }, [shiftData, arrivedDate, clockInDate, breakStartDate, breakEndDate]);

  /**
   * Reset to step 1 when modal closes to ensure fresh state on next open
   */
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setCurrentStep("step1");
    }
  }, [modalState.modalIsOpen]);

  /**
   * Close the bottom sheet and reset to step 1
   */
  const handleClose = () => {
    modalState.closeModal();
    setCurrentStep("step1");
  };

  /**
   * Navigate to the legacy needhelp page and close the bottom sheet.
   * Used when user clicks "No" on any step or "Speak to support" on step 3.
   *
   * Uses the path constant `DeprecatedGlobalAppV1Paths.NEED_HELP` instead of `match.url`
   * to ensure correct navigation regardless of where the bottom sheet is rendered
   * (Account page, Chat page, or any other route). This prevents navigation errors
   * when the component is used from different routes.
   *
   * Path: `/home/account/needhelp` - the guided support flow page
   */
  const navigateToNeedHelp = () => {
    handleClose();
    logEvent(APP_V2_USER_EVENTS.GET_HELP_DURING_SHIFT_CONTACTED_SUPPORT, { workerId: userId });
    history.push(DeprecatedGlobalAppV1Paths.NEED_HELP);
  };

  /**
   * Step 1: User clicks "Yes" - proceed to step 2 to show detailed shift information
   */
  const handleStep1Yes = () => {
    setCurrentStep("step2");
  };

  /**
   * Step 1: User clicks "No" - navigate to legacy needhelp page
   */
  const handleStep1No = () => {
    navigateToNeedHelp();
  };

  /**
   * Step 2: User clicks "Yes" - proceed to step 3 to show options for fixing timesheet
   */
  const handleStep2Yes = () => {
    setCurrentStep("step3");
  };

  /**
   * Step 2: User clicks "No" - navigate to legacy needhelp page
   */
  const handleStep2No = () => {
    navigateToNeedHelp();
  };

  /**
   * Step 3: User clicks "Got it" - navigate to the focus screen (timekeeping page)
   * for the in-progress shift so they can see their shift details and clock out
   */
  const handleStep3GotIt = () => {
    handleClose();
    logEvent(APP_V2_USER_EVENTS.GET_HELP_DURING_SHIFT_ACCEPTED_HELP, { workerId: userId });
    if (shiftData?.shift._id) {
      const focusScreenPath = generatePath(SHIFT_DETAILS_TIMEKEEPING_FULL_PATH, {
        shiftId: shiftData.shift._id,
      });
      history.push(focusScreenPath);
    }
  };

  /**
   * Step 3: User clicks "Speak to support" - navigate to legacy needhelp page
   */
  const handleStep3SpeakToSupport = () => {
    navigateToNeedHelp();
  };

  /**
   * Background gradient matching the Clipboard Score cancellation bottom sheet design
   * for visual consistency across the app
   */
  const getBackgroundGradient = (): string => {
    return "linear-gradient(180deg, rgba(250, 248, 247, 0.00) 0%, #FFF 72.7%), linear-gradient(117deg, #F6F3F1 18.83%, rgba(249, 225, 225, 0.84) 96.06%)";
  };

  return (
    <BottomSheet modalState={modalState} onClose={handleClose}>
      <IconButton
        size="small"
        iconType="close"
        variant="outlined"
        sx={{ position: "absolute", top: 24, right: 16, zIndex: 1 }}
        onClick={handleClose}
      />
      <DialogContent
        sx={{
          px: 5,
          py: 6,
          pt: 10,
          background: getBackgroundGradient(),
        }}
      >
        {/* Step 1: Initial question with shift details */}
        {currentStep === "step1" && (
          <Stack spacing={6} sx={{ width: "100%" }}>
            <Box sx={{ pr: 8, textAlign: "center" }}>
              <Text variant="h2" textAlign="center">
                Need help with this shift?
              </Text>
            </Box>

            {/* Display rich shift information matching the Bookings page design */}
            {shiftData ? (
              <Card
                outlined
                variant="quaternary"
                sx={{
                  width: "100%",
                  p: 6,
                }}
              >
                <Stack spacing={5}>
                  {/* Time Slot Indicator and Summary Info */}
                  <Stack direction="row" spacing={5} alignItems="flex-start">
                    <Box sx={{ flexShrink: 0 }}>
                      <TimeSlotIndicator
                        timeSlot={shiftPeriodToTimeSlot[shiftData.shift.name] ?? "DAY"}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ShiftCardSummaryInfo
                        showBreakInfo
                        showDay
                        showTimeRange
                        dateRange={{
                          startDate: parseISO(shiftData.shiftStart),
                          endDate: parseISO(shiftData.shiftEnd),
                        }}
                        timezone={shiftData.workplaceTimezone}
                        deviceTimezone={shiftData.workerTimezone}
                        durationInHours={shiftDurationData.totalDurationInHours}
                        breakDurationInMinutes={shiftDurationData.mandatoryBreakDurationInMinutes}
                        requiresBreak={shiftDurationData.requiresBreak}
                        workplaceName={shiftData.facilityName}
                        minutesElapsed={minutesElapsed}
                      />
                    </Box>
                  </Stack>
                </Stack>
              </Card>
            ) : (
              <Text color={(theme) => theme.palette.text.secondary} textAlign="center">
                Loading shift details...
              </Text>
            )}
          </Stack>
        )}

        {/* Step 2: Detailed shift information including timeline, facility, and shift details */}
        {currentStep === "step2" && (
          <Stack spacing={6} sx={{ width: "100%" }}>
            <Box sx={{ pr: 8, textAlign: "center" }}>
              <Stack spacing={0} alignItems="center">
                <Text variant="h2" textAlign="center">
                  Are you reaching out
                </Text>
                <Text variant="h2" textAlign="center">
                  about a timesheet issue?
                </Text>
              </Stack>
            </Box>

            {shiftData ? (
              <Stack spacing={6}>
                {/* Activity Timeline - Highlighted with primary border to draw attention */}
                {activityItems.length > 0 && (
                  <Card
                    outlined
                    variant="quaternary"
                    sx={{
                      width: "100%",
                      p: 6,
                      borderWidth: 2,
                      borderStyle: "solid",
                      borderColor: (theme) => theme.palette.primary.main,
                    }}
                  >
                    <Stack spacing={5}>
                      <Text semibold variant="h6">
                        Timeline
                      </Text>
                      <ActivityList activities={activityItems} />
                    </Stack>
                  </Card>
                )}

                {/* Facility Details */}
                <Card outlined variant="quaternary" sx={{ width: "100%", p: 6 }}>
                  <Stack spacing={5}>
                    <Text semibold variant="h6">
                      Facility Details
                    </Text>
                    <Stack spacing={4}>
                      <Stack direction="row" spacing={3} alignItems="flex-start">
                        <CbhIcon type="map-pin" size="small" />
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                          <Text semibold variant="body1">
                            {shiftData.facilityName}
                          </Text>
                          {shiftData.shift.facility.type && (
                            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                              {shiftData.shift.facility.type}
                            </Text>
                          )}
                          {shiftData.shift.facility.fullAddress?.formatted && (
                            <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                              {shiftData.shift.facility.fullAddress.formatted}
                            </Text>
                          )}
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>

                {/* Shift Details */}
                <Card outlined variant="quaternary" sx={{ width: "100%", p: 6 }}>
                  <Stack spacing={5}>
                    <Text semibold variant="h6">
                      Shift Details
                    </Text>
                    <Stack spacing={4}>
                      <Stack direction="row" spacing={3} alignItems="flex-start">
                        <CbhIcon type="clock" size="small" />
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                            Start time
                          </Text>
                          <Text semibold variant="body1">
                            {formatTimeWithTimezone(
                              parseISO(shiftData.shiftStart),
                              shiftData.workplaceTimezone,
                              false
                            )}
                          </Text>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={3} alignItems="flex-start">
                        <CbhIcon type="clock" size="small" />
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                            End time
                          </Text>
                          <Text semibold variant="body1">
                            {formatTimeWithTimezone(
                              parseISO(shiftData.shiftEnd),
                              shiftData.workplaceTimezone,
                              false
                            )}
                          </Text>
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={3} alignItems="flex-start">
                        <CbhIcon type="clock" size="small" />
                        <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                          <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                            Duration
                          </Text>
                          <Text semibold variant="body1">
                            {shiftDurationData.totalDurationInHours}h
                            {shiftDurationData.mandatoryBreakDurationInMinutes > 0 &&
                              ` incl. ${shiftDurationData.mandatoryBreakDurationInMinutes}min unpaid break`}
                          </Text>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            ) : (
              <Text color={(theme) => theme.palette.text.secondary} textAlign="center">
                Loading shift details...
              </Text>
            )}
          </Stack>
        )}

        {/* Step 3: Present two options for fixing timesheet issues */}
        {currentStep === "step3" && (
          <Stack spacing={6} sx={{ width: "100%" }}>
            <Box sx={{ pr: 8, textAlign: "center" }}>
              <Stack spacing={1} alignItems="center">
                <Text variant="h2" textAlign="center">
                  You have two options
                </Text>
                <Text variant="h2" textAlign="center">
                  to fix this
                </Text>
              </Stack>
            </Box>

            <Stack spacing={5}>
              {/* Option 1: Digital Timesheet - Preferred option with green border */}
              {/* This is the recommended approach: wait until shift ends and edit digitally */}
              <Card
                outlined
                variant="quaternary"
                sx={{
                  width: "100%",
                  p: 6,
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: (theme) => theme.palette.success.main,
                }}
              >
                <Stack spacing={4}>
                  <Text semibold variant="body2" color={(theme) => theme.palette.text.secondary}>
                    Option 1
                  </Text>
                  <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Text semibold variant="h6">
                        Wait until shift ends
                      </Text>
                      <Pill
                        size="small"
                        label="Recommended"
                        backgroundColor={(theme) => theme.palette.success.main}
                        color={(theme) => theme.palette.success.contrastText}
                        iconColor={(theme) => theme.palette.success.contrastText}
                        sx={{ flexShrink: 0 }}
                      />
                    </Stack>
                    <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                      Once your shift ends, you can edit all your timesheet hours digitally.
                      You&apos;ll be able to make changes to your shift start time, end time, and
                      break time. Everything will be editable and your information will be updated
                      once you submit your digital timesheet.
                    </Text>
                  </Stack>
                </Stack>
              </Card>

              {/* Option 2: Paper Timesheet - Alternative option for submitting via paper form */}
              <Card
                outlined
                variant="quaternary"
                sx={{
                  width: "100%",
                  p: 6,
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: (theme) => theme.palette.grey[400],
                }}
              >
                <Stack spacing={4}>
                  <Text semibold variant="body2" color={(theme) => theme.palette.text.secondary}>
                    Option 2
                  </Text>
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    <CbhIcon type="document" size="small" />
                    <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Text semibold variant="h6">
                          Submit a Paper Timesheet at the End of Your Shift
                        </Text>
                        <CbhIcon type="checkmark" size="small" />
                      </Stack>
                      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                        Enter all your hours via a paper timesheet. Download the paper timesheet PDF
                        and submit it to get paid for this shift.
                      </Text>
                      <Link
                        sx={{ alignSelf: "flex-start" }}
                        onClick={async () => {
                          await openArticle(paperTimesheetInstructionsSupportLink);
                        }}
                      >
                        Download paper timesheet PDF
                      </Link>
                    </Stack>
                  </Stack>
                </Stack>
              </Card>

              {/* Reassuring Note - Provides confidence about payment and Clipboard score points */}
              <Card
                outlined
                variant="quaternary"
                sx={{
                  width: "100%",
                  p: 6,
                  borderWidth: 2,
                  borderStyle: "solid",
                  borderColor: (theme) => theme.palette.primary.main,
                  backgroundColor: (theme) => theme.palette.primary.light + "20",
                }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <CbhIcon
                    type="checkmark"
                    size="medium"
                    sx={{ color: (theme) => theme.palette.primary.main, flexShrink: 0 }}
                  />
                  <Text semibold variant="body1" color={(theme) => theme.palette.text.primary}>
                    Please note you will get paid instantly after you submit your timesheet, and all
                    your Clipboard score points will be given based on the hours submitted.
                  </Text>
                </Stack>
              </Card>
            </Stack>
          </Stack>
        )}
      </DialogContent>

      {/* Step 1 Footer */}
      {currentStep === "step1" && (
        <DialogFooter orientation="vertical" sx={{ marginTop: 6 }}>
          <Button fullWidth variant="contained" size="large" onClick={handleStep1Yes}>
            Yes
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleStep1No}>
            No
          </Button>
        </DialogFooter>
      )}

      {/* Step 2 Footer */}
      {currentStep === "step2" && (
        <DialogFooter orientation="vertical" sx={{ marginTop: 6 }}>
          <Button fullWidth variant="contained" size="large" onClick={handleStep2Yes}>
            Yes
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleStep2No}>
            No
          </Button>
        </DialogFooter>
      )}

      {/* Step 3 Footer */}
      {currentStep === "step3" && (
        <DialogFooter orientation="vertical" sx={{ marginTop: 6 }}>
          <Button fullWidth variant="contained" size="large" onClick={handleStep3GotIt}>
            Got it
          </Button>
          <Button fullWidth variant="outlined" size="large" onClick={handleStep3SpeakToSupport}>
            Speak to support
          </Button>
        </DialogFooter>
      )}
    </BottomSheet>
  );
}
/* eslint-enable max-lines */
